import "./BookAppointment.scss";
import {useTranslation} from "react-i18next";
import React, {useEffect, useState} from "react";
import CustomButton from "../../../components/UI/CustomButton/CustomButton";
import arrowRight from "../../../assets/images/icons/arrow-right-3.svg";
import {
    IClass,
    IClassEventForAppointment, IRequestModelForAppointmentPrice,
    IRequestModelForAppointmentTime, IRequestModelForBuyingAppointmentByCash,
    ITimeForAppointment
} from "../../../models/ClassEventReservation";
import Spinner from "../../../components/Spiner/Spinner";
import {AppointmentService} from "../../../services/appointment.service";
import {textCropping} from "../../../helpers/language.helper";
import {getImageFromAuthServer, getProfileImage} from "../../../helpers/image.helper";
import {IInstructorClasses} from "../../../models/Trainer";
import {Calendar} from "react-calendar";
import moment from "moment";
import useAppSelector from "../../../hooks/useAppSelector";
import {ClassesService} from "../../../services/classes.service";
import {PurchaseTypes} from "../../../models/Payment";
import PaymentMethodsPopup from "../../../components/PaymentMethodsPopup/PaymentMethodsPopup";
import StripeElements from "../../../components/StripeElements/StripeElements";
import useAppDispatch from "../../../hooks/useAppDispatch";
import {openMessage} from "../../../store/notification/notificationSlice";
import {ICoupon, ICouponError} from "../../../models/Coupon";
import CouponForm from "../../../components/CouponForm/CouponForm";
import LoyaltyPointsExchanges from "../../../components/LoyaltyPointsExchanges/LoyaltyPointsExchanges";
import useDisableScroll from "../../../hooks/useDisableScroll";


interface IBookAppointmentProps {
    closeAppointmentPopup: () => void;
}

type ValuePiece = Date | null;
export type Value = ValuePiece | [ValuePiece, ValuePiece];

const BookAppointment: React.FC<IBookAppointmentProps> = ({closeAppointmentPopup}) => {
    useDisableScroll();
    const dispatch = useAppDispatch();
    const {t} = useTranslation();
    const {gymSettings} = useAppSelector(state => state.gymSettingsSlice);
    const {profile, isAuthorized} = useAppSelector(state => state.profileSlice);
    const {activeGym} = useAppSelector(state => state.multiGymsSlice);

    const [classesList, setClassesList] = useState<IClassEventForAppointment[]>([]);
    const [instructorList, setInstructorList] = useState<IInstructorClasses[]>([]);
    const [availableTimeList, setAvailableTimeList] = useState<ITimeForAppointment[]>([]);

    const [selectedClass, setSelectedClass] = useState<IClassEventForAppointment | null>(null);
    const [selectedInstructor, setSelectedInstructor] = useState<IInstructorClasses | null>(null);
    const [selectedDate, setSelectedDate] = useState<Value>(new Date());
    const [selectedTime, setSelectedTime] = useState<ITimeForAppointment | null>(null);

    const [classInfo, setClassInfo] = useState<IClass | null>(null);
    const [coupon, setCoupon] = useState<ICoupon | ICouponError | {}>({});
    const [loyaltyExchangeId, setLoyaltyExchangeId] = useState<number | null>(null);
    const [finalPrice, setFinalPrice] = useState<number>(0);

    const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isEmpty, setIsEmpty] = useState<boolean>(false);

    const [showPaymentMethodPopup, setShowPaymentMethodPopup] = useState<{
        visible: boolean,
        orderParams: any
    }>({visible: false, orderParams: null});

    useEffect(() => {
        if (!isAuthorized) {
            closeAppointmentPopup();
            return;
        }
        setIsEmpty(false);
        if (step === 1) {
            getClassList();
        } else if (step === 2) {
            if (!selectedClass) {
                setStep(1);
                return;
            }
            getTrainerList(selectedClass);
        } else if (step === 4) {
            if (!selectedClass || !selectedInstructor) {
                setStep(1);
                return;
            }
            const model: IRequestModelForAppointmentTime = {
                courseId: selectedClass.id,
                day: moment(selectedDate?.toString()).format("YYYY-MM-DD"),
                instructor: selectedInstructor.instructorId,
            }
            getAvailableTimeList(model);
        } else if (step === 5) {
            if (!selectedClass) {
                setStep(1);
                return;
            }
            getClassDataById(selectedClass.id);
        }
    }, [step, isAuthorized]);

    useEffect(() => {
        getFinalPrice();
    }, [coupon, loyaltyExchangeId]);

    const getPopupTitle = () => {
        let title = "";
        switch (step) {
            case 1:
                title = t("SelectService");
                break;
            case 2:
                title = t("SelectInstructor");
                break;
            case 3:
                title = t("SelectDate");
                break;
            case 4:
                title = t("SelectTime");
                break;
            case 5:
                title = t("ReviewAndPay");
                break;
            default:
                title = t("SelectService");
        }
        return title;
    }

    const clickOnPreviousBtn = () => {
        if (step === 2) {
            setSelectedInstructor(null);
        } else if (step === 3) {
            setSelectedDate(new Date());
        }
        setStep(step - 1);
    }

    const checkIsNextButtonDisabled = () => {
        if (step === 1) {
            if (!selectedClass) return true;
        } else if (step === 2) {
            if (!selectedInstructor) return true;
        } else if (step === 3) {
            if (!selectedDate) return true;
        } else if (step === 4) {
            if (!selectedTime) return true;
        } else {
            return false;
        }
    }

    const clickOnNextBtn = () => {
        if (step === 1) {
            if (!selectedClass) return;
            setStep(2);
        } else if (step === 2) {
            if (!selectedInstructor) return;
            setStep(3);
        } else if (step === 3) {
            if (!selectedDate) return;
            setStep(4);
        } else if (step === 4) {
            if (!selectedTime) return;
            setStep(5);
            getFinalPrice();
        }
    }

    const getClassList = async () => {
        try {
            setIsLoading(true);
            const response = await AppointmentService.getClassesForAppointments();
            setIsLoading(false);
            if (response.status === 200 && response.data.length) {
                setClassesList(response.data);
            } else {
                setIsEmpty(true);
            }

        } catch (e) {
            setIsLoading(false);
            setIsEmpty(true);
        }
    }

    const getTrainerList = async (item: IClassEventForAppointment) => {
        try {
            setIsLoading(true);
            const response = await AppointmentService.getInstructorsForAppointment();
            if (response.status === 200) {
                const instructors = response.data.filter((el) => el.instructor && el.trainningCourseId === item.id);
                setIsLoading(false);
                if (instructors.length) {
                    setInstructorList(instructors);
                } else {
                    setIsEmpty(true);
                }
            }
        } catch (e) {
            setIsLoading(false);
            setIsEmpty(true);
        }
    }

    const getAvailableTimeList = async (model: IRequestModelForAppointmentTime) => {
        try {
            setIsLoading(true);
            const response = await AppointmentService.getAvailableTime(model);
            setIsLoading(false);
            if (response.data.length && response.status === 200) {
                setAvailableTimeList(response.data)
            } else {
                setIsEmpty(true);
            }
        } catch (e) {
            setIsEmpty(true);
            setIsLoading(false);
        }

    }

    const getClassDataById = async (id: number) => {
        try {
            setIsLoading(true);
            const response = await ClassesService.getClassById(id);
            setIsLoading(false);
            if (response.data && response.status === 200) {
                setClassInfo(response.data)
            } else {
                setIsEmpty(true);
            }
        } catch (e) {
            setIsEmpty(true);
            setIsLoading(false);
        }
    }

    const getFinalPrice = async () => {
        if (!selectedInstructor) {
            setStep(2);
            setSelectedDate(null);
            setSelectedTime(null);
            return;
        }
        if (!selectedClass) {
            setStep(1);
            setSelectedInstructor(null);
            setSelectedTime(null);
            setSelectedDate(null);
            return;
        }
        let couponCode = null;
        if ("couponCode" in coupon && coupon.couponCode) {
            couponCode = coupon.couponCode;
        }
        const model: IRequestModelForAppointmentPrice = {
            instructorId: selectedInstructor?.instructor.id,
            couponCode,
            loyaltyExchangeId,
            visitors: null,
            trainingCourseId: selectedClass.id
        };
        setIsLoading(true);
        try {
            const response = await AppointmentService.getAppointmentPrice(model);
            setIsLoading(false);
            if (response.data.success === false && response.data.error) {
                dispatch(openMessage({type: "error", title: t(response.data.error.message)}));
                return;
            }
            setFinalPrice(response.data.prices.total);
        } catch (e) {
            dispatch(openMessage({type: "error", title: "ERROR_WHILE_CALCULATE_PRICE"}));
            setIsLoading(false);
            setFinalPrice(0);
        }
    }

    const clickOnPay = () => {
        let couponCode = null;
        if ("couponCode" in coupon && coupon.couponCode) {
            couponCode = coupon.couponCode;
        }
        const orderParams = {
            attendanceDate: selectedTime?.fromDate,
            courseId: selectedClass?.id,
            customerId: profile?.id,
            instructorId: selectedInstructor?.instructorId,
            returnUrl: `${window.location.protocol}//${window.location.hostname}/schedule`,
            loyaltyExchangeId,
            couponCode
        };
        setShowPaymentMethodPopup({visible: true, orderParams});
    }

    const buyAppointmentByCash = async () => {
        if (!profile) return;
        if (!selectedTime) {
            setStep(3);
            setSelectedDate(null);
            setSelectedTime(null);
            return;
        }
        if (!selectedInstructor) {
            setStep(2);
            setSelectedDate(null);
            setSelectedTime(null);
            return;
        }
        if (!selectedClass) {
            setStep(1);
            setSelectedInstructor(null);
            setSelectedTime(null);
            setSelectedDate(null);
            return;
        }
        let couponCode = null;
        if ("couponCode" in coupon && coupon.couponCode) {
            couponCode = coupon.couponCode;
        }
        const model: IRequestModelForBuyingAppointmentByCash = {
            instructorId: selectedInstructor.instructor.id,
            customerId: profile.id,
            courseId: selectedClass.id,
            attendanceDate: selectedTime.fromDate,
            isCreateInvoice: true,
            loyaltyExchangeId,
            couponCode,
            remarks: null
        }
        try {
            const response = await AppointmentService.buyAppointmentByCash(model);
            if (response.data.success === false && response.data.error) {
                dispatch(openMessage({type: "error", title: t(response.data.error.message)}));
                return;
            }
            if (response.data.appointmentId) {
                dispatch(openMessage({type: "success", title: t("AppointmentWasBoughtSuccessfully")}));
            } else {
                dispatch(openMessage({type: "error", title: t("FailedToBuyAppointment")}));
            }
        } catch (e) {
            dispatch(openMessage({type: "error", title: t("FailedToBuyAppointment")}));
        }
        closeAppointmentPopup();
    }

    const hidePaymentsPopup = () => {
        setShowPaymentMethodPopup({visible: false, orderParams: null})
    }

    const getAppointmentPrice = () => {
        if (!classInfo || !selectedInstructor || !gymSettings) return 0;
        const instructorAppointment = classInfo.appointmentInstructors.find(
            (x) => x.instructorId === selectedInstructor.instructorId
        );
        if (!instructorAppointment) return 0;
        return instructorAppointment.costPerHour;
    }

    return (
        <>
            <div className="book-appointment-container">
                <div className={`popup ${"step" + step}`}>
                    <div className={"popup-top"}>
                        <h3 className={"popup-top__title"}>{getPopupTitle()}</h3>
                        <div className={"steps-container"}>
                            <p className={"text"}>{t("Step")} {step} {t("Of")} 5</p>
                            <div className={"lines-wrapper"}>
                                <div className={`line ${step >= 1 && "active"}`}></div>
                                <div className={`line ${step >= 2 && "active"}`}></div>
                                <div className={`line ${step >= 3 && "active"}`}></div>
                                <div className={`line ${step >= 4 && "active"}`}></div>
                                <div className={`line ${step >= 5 && "active"}`}></div>
                            </div>
                        </div>
                    </div>
                    <div className={"popup-content"}>
                        {
                            isLoading ? (
                                <div className={"spinner-wrapper"}>
                                    <Spinner/>
                                </div>

                            ) : (isEmpty) ? (
                                <div className={"spinner-wrapper"}>
                                    <p>{t("NothingToDisplay")}</p>
                                </div>
                            ) : step === 1 ? (
                                <ul className={"wrapper"}>
                                    {
                                        classesList.map(item => {
                                            return (
                                                <li key={item.id}
                                                    className={`item ${selectedClass?.id === item.id && "selected"}`}
                                                    onClick={() => setSelectedClass(item)}>
                                                    <div className={"image"}
                                                         style={{backgroundImage: `url(${getImageFromAuthServer(item.image)})`}}></div>
                                                    <h4 className={"title"}>{item.name}</h4>
                                                    <p className={"text"}>{textCropping(item.description, 110)}</p>
                                                </li>
                                            )
                                        })
                                    }
                                </ul>
                            ) : (step === 2 ? (
                                <ul className={"wrapper"}>
                                    {
                                        instructorList.map(item => {
                                            return (
                                                <li key={item.instructorId}
                                                    className={`item ${selectedInstructor?.instructorId === item.instructorId && "selected"}`}
                                                    onClick={() => setSelectedInstructor(item)}>
                                                    <div className={"image"}
                                                         style={{backgroundImage: `url(${getProfileImage(activeGym.endpoint, item.instructor.profile)})`}}></div>
                                                    <h4 className={"title"}>{item.instructor.profile.firstname} {item.instructor.profile.lastname}</h4>
                                                    {
                                                        item.instructor.profile.bio && (
                                                            <p className={"text"}>{textCropping(item.instructor.profile.bio, 110)}</p>
                                                        )
                                                    }
                                                </li>
                                            )
                                        })
                                    }
                                </ul>
                            ) : (step === 3) ? (
                                <Calendar
                                    className="date-picker"
                                    onChange={setSelectedDate}
                                    value={selectedDate}
                                    formatMonthYear={(locale, date) => moment(date).format('MMMM')}
                                    showDoubleView={false}
                                    minDate={new Date()}
                                />
                            ) : (step === 4) ? (
                                <ul className={`wrapper ${"step" + step}`}>
                                    {availableTimeList.map(item => {
                                        return (
                                            <li key={item.id}
                                                className={`time-item ${selectedTime?.id === item.id && "active"}`}
                                                onClick={() => setSelectedTime(item)}>
                                                <p className={"time"}>{moment(item.fromDate).format("LT")} - {moment(item.toDate).format("LT")}</p>
                                            </li>
                                        )
                                    })}
                                </ul>
                            ) : (step === 5) ? (
                                <>
                                    <ul className={"result-wrapper"}>
                                        <li className={"result-item"}>
                                            <h4>{t("Class")}</h4>
                                            <p>{selectedClass?.name}</p>
                                        </li>
                                        <li className={"result-item"}>
                                            <h4>{t("Time")}</h4>
                                            <p>{moment(selectedTime?.fromDate).format("LT")}</p>
                                        </li>
                                        <li className={"result-item"}>
                                            <h4>{t("Instructor")}</h4>
                                            <p>{selectedInstructor?.instructor.profile.firstname} {selectedInstructor?.instructor.profile.lastname}</p>
                                        </li>
                                        <li className={"result-item"}>
                                            <h4>{t("Venue")}</h4>
                                            <p>{classInfo?.trainningRoom.name}</p>
                                        </li>

                                        <li className={"result-item"}>
                                            <h4>{t("Date")}</h4>
                                            <p>{moment(selectedDate?.toString()).format("DD MMMM yyyy")}</p>
                                        </li>

                                        <li className={"result-item"}>
                                            <h4>{t("Price")}</h4>
                                            <p>{gymSettings?.defaultCurrencyCode}{finalPrice}</p>
                                        </li>
                                    </ul>
                                    <div className={"coupon"}>
                                        <CouponForm
                                            coupon={coupon}
                                            setCoupon={setCoupon}
                                            scope={"appointment"}
                                        />
                                    </div>
                                    {classInfo && gymSettings?.enableLoyaltyPoints && (
                                        <div className={"loyalty"}>
                                            <LoyaltyPointsExchanges
                                                itemPrice={getAppointmentPrice()}
                                                setActiveLoyaltyExchangeId={setLoyaltyExchangeId}
                                                activeLoyaltyExchangeId={loyaltyExchangeId}
                                            />
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className={"spinner-wrapper"}>
                                    <p>{t("NothingToDisplay")}</p>
                                </div>
                            ))
                        }
                    </div>
                    <div className={"popup-bottom"}>
                        <button onClick={closeAppointmentPopup} className={"cancel"}>{t("Cancel")}</button>

                        {
                            step !== 1 && step !== 5 && (
                                <CustomButton
                                    text={t("PreviousStep")}
                                    type={"button"}
                                    typeStyle={"outline"}
                                    borderRadius={false}
                                    blueForOutline={true}
                                    functionOnClick={clickOnPreviousBtn}
                                />
                            )
                        }

                        {
                            selectedClass && !selectedClass.prepayAppointments && step === 5 && (
                                <CustomButton
                                    text={t("PayByCash")}
                                    type={"button"}
                                    typeStyle={"outline"}
                                    borderRadius={false}
                                    blueForOutline={true}
                                    functionOnClick={buyAppointmentByCash}
                                />
                            )
                        }

                        <CustomButton
                            text={step === 5 ? t("PayNow") : t("NextStep")}
                            type={"button"}
                            typeStyle={"fill"}
                            borderRadius={false}
                            functionOnClick={step === 5 ? clickOnPay : clickOnNextBtn}
                            altIcon={step === 5 ? undefined : "next"}
                            iconPath={step === 5 ? undefined : arrowRight}
                            disabled={checkIsNextButtonDisabled()}
                        />


                    </div>
                </div>
            </div>
            {
                showPaymentMethodPopup.visible && (
                    <StripeElements>
                        <PaymentMethodsPopup
                            closePopupFn={hidePaymentsPopup}
                            functionOnContinue={() => {
                                hidePaymentsPopup()
                                closeAppointmentPopup()
                            }}
                            purchasingType={PurchaseTypes.Appointment}
                            orderParams={showPaymentMethodPopup.orderParams}
                        />
                    </StripeElements>

                )
            }
        </>

    )
}

export default BookAppointment;